import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogApi } from '../api';
import { CreateCatalogVersionDialog, PublishCatalogDialog } from '../components/catalog';
import {
  ChevronRight,
  Calendar,
  User,
  CheckCircle,
  Clock,
  AlertTriangle,
  Loader2,
  Plus,
  Eye,
  Edit,
  Trash2,
  FileJson,
} from 'lucide-react';
import clsx from 'clsx';
import { usePublicSettings } from '../hooks/usePublicSettings';

type StatusFilter = 'all' | 'draft' | 'published';

export function CatalogVersionsPage() {
  const queryClient = useQueryClient();
  const { get: getSetting } = usePublicSettings();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<number | null>(null);
  const [versionToPublish, setVersionToPublish] = useState<number | null>(null);

  const itemsPerPage = 10;
  const offset = (currentPage - 1) * itemsPerPage;

  // Fetch catalog versions
  const { data, isLoading, error } = useQuery({
    queryKey: ['catalog', 'versions', { status: statusFilter, limit: itemsPerPage, offset }],
    queryFn: () => catalogApi.getVersions({
      status: statusFilter === 'all' ? undefined : statusFilter,
      limit: itemsPerPage,
      offset,
    }),
  });

  const versions = data?.data.data || [];
  const totalCount = data?.data.pagination?.total || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (version: number) => catalogApi.delete(version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog', 'versions'] });
      setVersionToDelete(null);
    },
  });

  const handleDelete = async (version: number) => {
    try {
      await deleteMutation.mutateAsync(version);
    } catch (error) {
      console.error('Failed to delete version:', error);
    }
  };

  const handleFilterChange = (filter: StatusFilter) => {
    setStatusFilter(filter);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm text-gray-500">
        <Link to="/catalog" className="hover:text-gray-700">
          Catalog
        </Link>
        <ChevronRight className="w-4 h-4 mx-2" />
        <span className="text-gray-900 font-medium">Versions</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Catalog Versions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and publish catalog versions
          </p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Version
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">Status:</span>
          <button
            onClick={() => handleFilterChange('all')}
            className={clsx(
              'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              statusFilter === 'all'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            All
          </button>
          <button
            onClick={() => handleFilterChange('draft')}
            className={clsx(
              'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              statusFilter === 'draft'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            <Clock className="w-3 h-3 inline mr-1" />
            Draft
          </button>
          <button
            onClick={() => handleFilterChange('published')}
            className={clsx(
              'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              statusFilter === 'published'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            <CheckCircle className="w-3 h-3 inline mr-1" />
            Published
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Error loading versions
            </h3>
            <p className="text-gray-600">Please try again later</p>
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-12">
            <FileJson className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No versions found
            </h3>
            <p className="text-gray-600 mb-4">
              {statusFilter === 'all'
                ? 'Create your first catalog version to get started'
                : `No ${statusFilter} versions found`}
            </p>
            {statusFilter !== 'all' && (
              <button
                onClick={() => handleFilterChange('all')}
                className="btn-secondary"
              >
                Show all versions
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Published At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {versions.map((version) => {
                  const isDraft = version.status === 'draft';
                  const isPublished = version.status === 'published';

                  return (
                    <tr key={version.version} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FileJson className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            v{version.version}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={clsx(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            isPublished
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          )}
                        >
                          {isPublished ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Published
                            </>
                          ) : (
                            <>
                              <Clock className="w-3 h-3 mr-1" />
                              Draft
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          {version.createdBy ? (
                            <>
                              <User className="w-4 h-4 mr-1 text-gray-400" />
                              {version.createdBy.name}
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          {version.createdAt ? (
                            <>
                              <Calendar className="w-4 h-4 mr-1" />
                              {new Date(version.createdAt).toLocaleDateString('it-IT')}
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {version.publishedAt ? (
                          <div className="flex items-center text-sm text-gray-500">
                            <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
                            {new Date(version.publishedAt).toLocaleDateString('it-IT')}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* View button */}
                          <Link
                            to={`/catalog/versions/${version.version}`}
                            className="text-primary-600 hover:text-primary-900"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>

                          {/* Edit button - only for drafts */}
                          {isDraft && (
                            <Link
                              to={`/catalog/versions/${version.version}`}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </Link>
                          )}

                          {/* Publish button - only for drafts */}
                          {isDraft && (
                            <button
                              onClick={async () => {
                                if (getSetting<boolean>('catalog.confirmBeforePublish')) {
                                  setVersionToPublish(version.version);
                                } else {
                                  try {
                                    await catalogApi.publish(version.version);
                                    queryClient.invalidateQueries({ queryKey: ['catalog'] });
                                  } catch (err) {
                                    console.error('Failed to publish:', err);
                                  }
                                }
                              }}
                              className="text-green-600 hover:text-green-900"
                              title="Publish"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}

                          {/* Delete button - only for drafts */}
                          {isDraft && (
                            <button
                              onClick={() => setVersionToDelete(version.version)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">{offset + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(offset + itemsPerPage, totalCount)}
                        </span>{' '}
                        of <span className="font-medium">{totalCount}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter((page) => {
                            // Show first, last, current, and adjacent pages
                            return (
                              page === 1 ||
                              page === totalPages ||
                              Math.abs(page - currentPage) <= 1
                            );
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const prevPage = array[index - 1];
                            const showEllipsis = prevPage && page - prevPage > 1;

                            return (
                              <div key={page} className="inline-flex">
                                {showEllipsis && (
                                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                    ...
                                  </span>
                                )}
                                <button
                                  onClick={() => setCurrentPage(page)}
                                  className={clsx(
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page === currentPage
                                      ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  )}
                                >
                                  {page}
                                </button>
                              </div>
                            );
                          })}
                        <button
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {versionToDelete !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Version {versionToDelete}
                </h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this draft version? This action cannot be
                  undone.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setVersionToDelete(null)}
                className="btn-secondary"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(versionToDelete)}
                className="btn-danger"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Version Dialog */}
      <CreateCatalogVersionDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      />

      {/* Publish Version Dialog */}
      {versionToPublish !== null && (
        <PublishCatalogDialog
          isOpen={true}
          version={versionToPublish}
          onClose={() => setVersionToPublish(null)}
        />
      )}
    </div>
  );
}
